import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector, useStore, shallowEqual } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';

import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { consumeAuth0AppState } from '../../util/auth0FlexSession';
import { completeAuth0FlexSessionAndNavigate } from '../../util/auth0FlexSessionNavigation';
import { getAuth0RedirectUri, hasAuth0EnvConfigured } from '../../util/auth0Env';
import * as log from '../../util/log';

/**
 * After Auth0 Universal Login, exchanges the ID token for Sharetribe Flex cookies
 * and navigates to `from` (checkout) or the user-type default page (e.g. /s for customers).
 */
const Auth0FlexBridge = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();

  const { isAuthenticated: stAuth, authInfoLoaded } = useSelector(
    state => ({
      isAuthenticated: state.auth?.isAuthenticated,
      authInfoLoaded: state.auth?.authInfoLoaded,
    }),
    shallowEqual
  );

  const {
    isAuthenticated: auth0Auth,
    isLoading: auth0Loading,
    getAccessTokenSilently,
    getIdTokenClaims,
  } = useAuth0();

  const inFlight = useRef(false);

  useEffect(() => {
    if (!hasAuth0EnvConfigured()) {
      return;
    }
    if (!authInfoLoaded || auth0Loading) {
      return;
    }
    if (stAuth) {
      return;
    }
    if (!auth0Auth) {
      return;
    }
    if (inFlight.current) {
      return;
    }

    const run = async () => {
      inFlight.current = true;
      let idToken;
      const redirectUri = getAuth0RedirectUri();

      const pickIdTokenFromVerbose = verbose => {
        if (!verbose || typeof verbose !== 'object') {
          return null;
        }
        return verbose.id_token || verbose.idToken || null;
      };

      // Cache hits often return only the access token string; Flex needs the ID token JWT.
      try {
        const verbose = await getAccessTokenSilently({
          cacheMode: 'off',
          detailedResponse: true,
          authorizationParams: {
            scope: 'openid profile email',
            ...(redirectUri ? { redirect_uri: redirectUri } : {}),
          },
        });
        idToken =
          typeof verbose === 'string' ? null : pickIdTokenFromVerbose(verbose);
      } catch (e) {
        log.error(e, 'auth0-get-token-detailed-failed');
      }

      if (!idToken) {
        try {
          const claims = await getIdTokenClaims();
          idToken = claims && claims.__raw;
        } catch (e) {
          log.error(e, 'auth0-get-id-token-claims-failed');
        }
      }

      if (!idToken) {
        log.error(new Error('Auth0FlexBridge: could not resolve ID token'));
        inFlight.current = false;
        return;
      }

      const appState = consumeAuth0AppState();
      const { from, userType, defaultConfirm } = appState;

      try {
        await completeAuth0FlexSessionAndNavigate({
          idToken,
          from,
          userType,
          defaultConfirm,
          dispatch,
          history,
          store,
          routeConfiguration,
        });
      } catch (e) {
        log.error(e, 'auth0-flex-session-request-failed');
      } finally {
        inFlight.current = false;
      }
    };

    run();
  }, [
    auth0Auth,
    auth0Loading,
    authInfoLoaded,
    dispatch,
    getAccessTokenSilently,
    getIdTokenClaims,
    history,
    routeConfiguration,
    stAuth,
    store,
  ]);

  return null;
};

export default Auth0FlexBridge;
